import { createSelector, createSlice } from "@reduxjs/toolkit";
import { useSelector } from "react-redux";
import { ensureArray } from "../utils/arrayUtilities";
import { deepMerge } from "../utils/objectUtilities";
import { logout } from "./initContext";

export const pendingUpdateSlice = createSlice({
    name: 'pendingUpdates',
    initialState: {
        queue: [],
        entityUpdates: {}
    },
    reducers: {
        addDelta: (state, action) => {
            ensureArray(action.payload).forEach(({ entity, pk, fk, requestId, delta, successAction, failureAction, url, method, body }) => {
                if (!state.entityUpdates[entity]) {
                    state.entityUpdates[entity] = {}
                }
                let base = state.entityUpdates[entity];
                if (fk) { // this supports foreign key / map to array constructs such as for notes, epcr vitals etc.
                    if (!state.entityUpdates[entity][fk]) {
                        state.entityUpdates[entity][fk] = {};
                    }
                    if (state.entityUpdates[entity][fk][pk]) {
                        state.entityUpdates[entity][fk][pk][0] = delta ? deepMerge(state.entityUpdates[entity][fk][pk][0], delta) : delta;
                    } else {
                        state.entityUpdates[entity][fk][pk] = [delta];
                    }  
                    base = base[fk];
                } else {
                    if (state.entityUpdates[entity][pk]) {
                        state.entityUpdates[entity][pk][0] = delta ? deepMerge(state.entityUpdates[entity][pk][0], delta) : delta;
                    } else {
                        state.entityUpdates[entity][pk] = [delta];
                    }
                }
                const pending = { requestId, delta, successAction, failureAction, url, method, body, pk, fk, entity };
                base[pk].push(pending);
                state.queue.push(pending);
            });
        },
        removeDelta: (state, action) => {
            ensureArray(action.payload).forEach(({ entity, pk, fk, requestId }) => {
                const base = fk ? state.entityUpdates[entity]?.[fk] : state.entityUpdates[entity];
                if (base) {
                    const element = base[pk];
                    const idx = element?.findIndex(e => e?.requestId === requestId);
                    if (idx > 0) {
                        element.splice(idx, 1);
                        if (element.length > 1) {
                            element[0] = element[element.length - 1] ? deepMerge({}, ...element.slice(1).map(e => e.delta)) : element[element.length - 1];
                        } else {
                            delete base[pk];
                        }
                    }
                }
                const idx = state.queue.findIndex(req => req.requestId === requestId);
                if (idx > -1) {
                    state.queue.splice(idx, 1);
                }
            })
        }
    },
    extraReducers: builder => {
        builder.addCase(logout, () => pendingUpdateSlice.getInitialState());
    }
});

const EMPTY_OBJECT = {};

export const addEntityDelta = (requestId, entity, pk, fk, delta, successAction, failureAction, url, method) => pendingUpdateSlice.actions.addDelta({ requestId, entity, pk, fk, delta, successAction, failureAction, url, method });
export const removeEntityDelta = (requestId, entity, pk, fk) => pendingUpdateSlice.actions.removeDelta({ entity, pk, fk, requestId });

const pendingUpdates = state => state.pendingUpdates;
export const selectEntityUpdates = createSelector([pendingUpdates, (state, entity) => entity], (pending, entity) => pending.entityUpdates[entity] ?? EMPTY_OBJECT);
const entityItemSelectors = new Map();

function memoizeMerge(mode, keyFn) {
    let lastBase = null;
    let lastPatch = null;
    let lastResult = null;

    return (base, patch) => {
        if (base === lastBase
            && lastPatch
            && Object.keys(patch).length === Object.keys(lastPatch).length
            && Object.entries(patch).every(([ key, data ]) => lastPatch[key] === data)
        ) {
            return lastResult;
        }

        let next;
        if (Object.keys(patch).length === 0) {
            next = base;
        } else {
            switch (mode) {
                case 'mapToArray':
                case 'array':
                    next = new Map(base.map(b => [keyFn(b), b]));
                    Object.entries(patch).forEach(([key, value]) => {
                        if (next.get(key) && value) {
                            next.set(key, deepMerge({}, next.get(key), value));
                        } else if (value) {
                            next.set(key, { ...value })
                        } else {
                            next.delete(key);
                        }
                    });
                    next = Array.from(next.values());
                    break;
                case 'map':
                    next = { ...base };
                    Object.entries(patch).forEach(([key, value]) => {
                        if (next[key] && value) {
                            next[key] = deepMerge({}, next[key], value);
                        } else if (value) {
                            next[key] = { ...value }
                        } else {
                            delete next[key];
                        }
                    });
                    break;
                case 'object': {
                    const key = keyFn(base);
                    if (Object.hasOwn(patch, key)) {
                        if (patch[key] && base) {
                            next = deepMerge({}, base, patch[key])
                        } else if (patch[key]) {
                            next = patch[key]
                        } else {
                            next = undefined
                        }
                    } else {
                        next = base;
                    }
                    break;
                }
                default:
                    throw new Error('Unknown mode to memoizeMerge ' + mode);
            }
        }

        lastBase = base;
        lastPatch = patch;
        lastResult = next;

        return next;
    };
}

export const createEagerSelector = (entity, { mode = 'map', key = element => element?.id } = {}) => {
    if (entityItemSelectors.has(entity)) {
        return entityItemSelectors.get(entity);
    }

    const memoMerge = memoizeMerge(mode, key);

    const factory = (baseSelector, outputFn = id => id) =>
        createSelector(
            [
                baseSelector,
                (state, foreignKey) => {
                    if (mode === 'mapToArray' && foreignKey) {
                        return state.pendingUpdates?.entityUpdates?.[entity]?.[foreignKey] ?? EMPTY_OBJECT
                    } else if (mode === 'mapToArray') {
                        // 'get all' selector likely used, so return all values as a single array
                        if (!state.pendingUpdates?.entityUpdates?.[entity]) {
                            return EMPTY_OBJECT;
                        }
                        const temp = Object.assign({}, ...Object.values(state.pendingUpdates.entityUpdates[entity]));
                        return temp;
                    }
                    return state.pendingUpdates?.entityUpdates?.[entity] ?? EMPTY_OBJECT
                },
                state => state.clientConfig.config
            ],
            (...args) => {
                const clientConfig = args.pop();
                const pending = args.pop();
                const base = outputFn(...args);
                if (!clientConfig?.enableOfflineMode) {
                    return base;
                }
                switch (mode) {
                    case 'mapToArray':
                    case 'array':
                    case 'map': {
                        const toMerge = {};
                        Object.entries(pending).forEach(([id, value]) => toMerge[id] = value[0]);
                        return Object.keys(pending).length > 0 ? memoMerge(base, toMerge) : base;
                    }
                    case 'object': {
                        const id = key(base);
                        return pending[id] ? memoMerge(base, { [id]: pending[id][0] }) : base;
                    }
                    default:
                        throw new Error(`Unknown eager selector mode "${mode}"`);
                }
            }
        );

    entityItemSelectors.set(entity, factory);
    return factory;
}

export const eagerMetaFactory = entity => (pk, { ...props } = {}) => ({ eagerMeta: { entity, pk, ...props }});

const selectPendingUpdates = state => state.pendingUpdates.queue;
export const usePendingUpdates = () => useSelector(selectPendingUpdates);

const reducerDetails = { name: pendingUpdateSlice.name, reducer: pendingUpdateSlice.reducer };
export default reducerDetails;
